/**
 * Excuse a requirement, or reword an excuse that already stands.
 *
 * ONE ENDPOINT, TWO ENTRANCES. Both modes post to `/waive`; the difference is
 * only what the operator is doing. Waiving stores the decision and the reason;
 * rewording stores a new reason and leaves `waived_at` exactly where it was —
 * the moment the exception was granted is a fact about the past and is not
 * re-stamped because somebody improved the wording. The frontend does not
 * enforce that: the write service does, and this dialog simply never sends a
 * timestamp.
 *
 * THE REASON IS MANDATORY, and it is mandatory here as well as on the server.
 * An excuse without a stated reason is the one entry in the audit trail that
 * cannot be read back later, and "the server will reject it" is a worse way to
 * learn that than a disabled button. Whitespace does not count — the value is
 * trimmed before it is judged and before it is sent.
 *
 * A DEPOSIT WAIVER IS A FINANCIAL EXCEPTION and says so. Same endpoint, same
 * state, different weight: nothing about the flow changes, only how plainly the
 * dialog states what is being decided.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { waiveRequirement } from "../../../api/adminData";
import { requirementTypeLabel } from "./requirementLabels";

/** Mirrors `RequirementWaiveRequest` — reason min 3, max 500. */
export const WAIVE_REASON_MIN = 3;
export const WAIVE_REASON_MAX = 500;

function Fact({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className="text-[12.5px] font-medium text-foreground">{children}</span>
    </div>
  );
}

export default function RequirementWaiveModal({ row, mode = "waive", onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    setReason(mode === "reason" ? row?.waive_reason || "" : "");
    setSubmitError(null);
    setPending(false);
  }, [row, mode]);

  if (!row) return null;

  const isDeposit = row.requirement_type === "deposit";
  const isReasonEdit = mode === "reason";
  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < WAIVE_REASON_MIN;
  const tooLong = trimmed.length > WAIVE_REASON_MAX;
  const canSubmit =
    !pending && trimmed.length >= WAIVE_REASON_MIN && !tooLong;

  const title = isReasonEdit
    ? "Begründung bearbeiten"
    : isDeposit
      ? "Kautionspflicht erlassen"
      : "Pflicht erlassen";

  async function handleSubmit() {
    if (!canSubmit) return;
    setPending(true);
    setSubmitError(null);
    try {
      await waiveRequirement(row.tenancy_id, row.requirement_type, {
        reason: trimmed,
      });
      toast.success(
        isReasonEdit ? "Begründung gespeichert." : "Pflicht erlassen."
      );
      onSaved();
    } catch (e) {
      const message =
        e?.message ||
        (isReasonEdit
          ? "Die Begründung konnte nicht gespeichert werden."
          : "Die Pflicht konnte nicht erlassen werden.");
      setSubmitError(message);
      toast.error(message);
      setPending(false);
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => (!open && !pending ? onClose() : null)}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {isReasonEdit
              ? "Die Begründung wird ersetzt. Der Zeitpunkt des Erlasses bleibt unverändert."
              : 'Der Status dieses Nachweises wird auf „Nicht erforderlich“ gesetzt.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-1.5 rounded-[8px] border border-border bg-muted/30 px-3 py-2.5">
          <Fact label="Nachweis">{requirementTypeLabel(row.requirement_type)}</Fact>
          <Fact label="Mieter">{row.tenant_name || "—"}</Fact>
          <Fact label="Mietverhältnis">{row.tenancy_reference_number || "—"}</Fact>
        </div>

        {isDeposit && !isReasonEdit ? (
          <p
            role="alert"
            className="m-0 rounded-[8px] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12.5px] text-amber-800 dark:text-amber-200"
          >
            Dies ist eine administrative bzw. finanzielle Ausnahme. Die
            Kautionspflicht wird für dieses Mietverhältnis als nicht erforderlich
            behandelt.
          </p>
        ) : null}

        <div className="grid gap-1.5">
          <label
            htmlFor="requirement-waive-reason"
            className="text-[12px] font-medium text-muted-foreground"
          >
            Begründung (erforderlich)
          </label>
          <textarea
            id="requirement-waive-reason"
            rows={3}
            maxLength={WAIVE_REASON_MAX + 1}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder="Warum ist dieser Nachweis nicht erforderlich?"
            className="w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          {tooShort ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Mindestens {WAIVE_REASON_MIN} Zeichen.
            </p>
          ) : null}
          {tooLong ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Höchstens {WAIVE_REASON_MAX} Zeichen.
            </p>
          ) : null}
          {submitError ? (
            <p
              role="alert"
              className="m-0 rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive"
            >
              {submitError}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter className="gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
          >
            Abbrechen
          </button>
          {/* Not `AlertDialogAction`: that primitive closes on click even when
              the submit is blocked, and it would close before the API answers —
              a 422 would then look like a successful waiver. */}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className={`inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium disabled:pointer-events-none disabled:opacity-50 ${
              isReasonEdit
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            }`}
          >
            {pending ? "Speichere …" : isReasonEdit ? "Begründung speichern" : title}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
